import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth as authAPI, user as userAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Failed to parse saved user:', e);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  const register = async (name, email, password) => {
    try {
      setError(null);
      const response = await authAPI.register({ name, email, password });
      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithSyncCode = async (syncCode) => {
    try {
      console.log('🔐 loginWithSyncCode called with:', syncCode);
      setError(null);
      const response = await authAPI.syncCode(syncCode);
      const { token, user: userData } = response.data;

      console.log('✅ Sync code API response received');
      console.log('🔑 Token received:', token ? token.substring(0, 20) + '...' : 'NONE');
      console.log('👤 User data received:', userData);

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      console.log('💾 Stored in localStorage');
      console.log('🔑 Verify token stored:', localStorage.getItem('authToken') ? 'YES' : 'NO');

      setUser(userData);
      console.log('✅ setUser called with userData');

      return { success: true };
    } catch (err) {
      console.error('❌ loginWithSyncCode failed:', err);
      const errorMessage = err.response?.data?.error || 'Invalid sync code';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  };

  const refreshUser = async () => {
    try {
      const response = await userAPI.getProfile();
      const userData = response.data;

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const subscribe = async () => {
    try {
      await userAPI.subscribe();
      await refreshUser();
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Subscription failed';
      return { success: false, error: errorMessage };
    }
  };

  const generateSyncCode = async () => {
    try {
      const response = await userAPI.generateSyncCode();
      const { syncCode } = response.data;
      await refreshUser();
      return { success: true, syncCode };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to generate sync code';
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async (googleIdToken, userRole = null, browserFingerprint = null) => {
    try {
      setError(null);

      // Use the new web signup/signin endpoint that auto-detects existing users
      const response = await authAPI.webSignupOrSignin({
        googleIdToken,
        userRole,
        browserFingerprint,
      });

      // If backend requires role selection (new user, first call)
      if (response.data.requiresRole) {
        return {
          success: false,
          requiresRole: true,
        };
      }

      const { token, user: userData, isNewUser } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return { success: true, isNewUser };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Google login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogleWeb = async (googleIdToken, userRole = null, browserFingerprint = null, redirectTo = null) => {
    try {
      setError(null);
      const response = await authAPI.webSignupOrSignin({
        googleIdToken,
        userRole,
        browserFingerprint,
        redirectTo,
      });

      const { token, user: userData, isNewUser, requiresRole, redirectTo: redirectUrl } = response.data;

      // Case 1: User needs to select role
      if (requiresRole) {
        return { success: false, requiresRole: true, redirectTo: redirectUrl };
      }

      // Case 2: User signed in or signed up successfully
      if (token && userData) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        return { success: true, isNewUser, redirectTo: redirectUrl };
      }

      // Unexpected response
      return { success: false, error: 'Unexpected response from server' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Authentication failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const isStudent = () => {
    return user?.userRole === 'student';
  };

  const isTeacher = () => {
    return user?.userRole === 'teacher';
  };

  const linkGoogle = async (googleIdToken) => {
    try {
      setError(null);
      await userAPI.linkGoogle(googleIdToken);
      await refreshUser();
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to link Google account';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    loginWithGoogle,
    loginWithGoogleWeb,
    loginWithSyncCode,
    logout,
    subscribe,
    generateSyncCode,
    linkGoogle,
    refreshUser,
    isStudent,
    isTeacher,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
